import React, { createContext, useContext, useReducer, useEffect } from 'react';

const AssessmentContext = createContext(null);

const initialState = {
  currentScreen: 'landing',
  currentQuestionIndex: 0,
  totalQuestions: 25,
  responses: {},
  archetypeScores: {
    performer: 0,
    ghost: 0,
    controller: 0,
    pleaser: 0,
    destroyer: 0
  },
  email: '',
  completionPercentage: 0,
  isTransitioning: false,
  lastSaved: null
};

const assessmentReducer = (state, action) => {
  switch (action?.type) {
    case 'START_ASSESSMENT':
      return {
        ...state,
        currentScreen: 'question-assessment',
        currentQuestionIndex: 0,
        completionPercentage: 0
      };

    case 'ANSWER_QUESTION':
      const newResponses = {
        ...state?.responses,
        [action?.payload?.questionId]: action?.payload?.answer
      };
      
      // Recalculate all archetype scores from scratch
      const newScores = {
        performer: 0,
        ghost: 0,
        controller: 0,
        pleaser: 0,
        destroyer: 0
      };
      
      // Sum up scores for each archetype based on all responses
      Object.entries(newResponses)?.forEach(([questionId, answerValue]) => {
        // Get the archetype mapping for this question from the payload
        // We need to pass all question mappings or reconstruct them here
        const archetypePoints = action?.payload?.allQuestions?.[questionId]?.archetypes;
        if (archetypePoints) {
          Object.entries(archetypePoints)?.forEach(([archetype, multiplier]) => {
            newScores[archetype] = (newScores?.[archetype] || 0) + (answerValue * multiplier);
          });
        }
      });

      return {
        ...state,
        responses: newResponses,
        archetypeScores: newScores,
        lastSaved: new Date()?.toISOString()
      };

    case 'NEXT_QUESTION':
      const nextIndex = state?.currentQuestionIndex + 1;
      const newPercentage = (nextIndex / state?.totalQuestions) * 100;
      
      if (nextIndex >= state?.totalQuestions) {
        return {
          ...state,
          currentScreen: 'email-capture',
          completionPercentage: 95
        };
      }

      return {
        ...state,
        currentQuestionIndex: nextIndex,
        completionPercentage: newPercentage
      };

    case 'PREVIOUS_QUESTION':
      return {
        ...state,
        currentQuestionIndex: Math.max(0, state?.currentQuestionIndex - 1),
        completionPercentage: (Math.max(0, state?.currentQuestionIndex - 1) / state?.totalQuestions) * 100
      };

    case 'SUBMIT_EMAIL':
      return {
        ...state,
        email: action?.payload?.email,
        currentScreen: 'personalized-results',
        completionPercentage: 100,
        lastSaved: new Date()?.toISOString()
      };

    case 'SET_SCREEN':
      return {
        ...state,
        currentScreen: action?.payload?.screen
      };

    case 'SET_TRANSITIONING':
      return {
        ...state,
        isTransitioning: action?.payload?.isTransitioning
      };

    case 'RESET_ASSESSMENT':
      return {
        ...initialState,
        lastSaved: new Date()?.toISOString()
      };

    case 'LOAD_STATE':
      return {
        ...state,
        ...action?.payload?.state
      };

    default:
      return state;
  }
};

export const AssessmentStateManager = ({ children }) => {
  const [state, dispatch] = useReducer(assessmentReducer, initialState);

  useEffect(() => {
    const savedState = localStorage.getItem('assessmentState');
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        dispatch({ type: 'LOAD_STATE', payload: { state: parsedState } });
      } catch (error) {
        console.error('Failed to load saved state:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (state?.lastSaved) {
      localStorage.setItem('assessmentState', JSON.stringify(state));
    }
  }, [state]);

  const startAssessment = () => {
    dispatch({ type: 'START_ASSESSMENT' });
  };

  const answerQuestion = (questionId, answer, archetypePoints, allQuestions) => {
    dispatch({ 
      type: 'ANSWER_QUESTION', 
      payload: { questionId, answer, archetypePoints, allQuestions } 
    });
  };

  const nextQuestion = () => {
    dispatch({ type: 'NEXT_QUESTION' });
  };

  const previousQuestion = () => {
    dispatch({ type: 'PREVIOUS_QUESTION' });
  };

  const submitEmail = (email) => {
    dispatch({ type: 'SUBMIT_EMAIL', payload: { email } });
  };

  const setScreen = (screen) => {
    dispatch({ type: 'SET_SCREEN', payload: { screen } });
  };

  const setTransitioning = (isTransitioning) => {
    dispatch({ type: 'SET_TRANSITIONING', payload: { isTransitioning } });
  };

  const resetAssessment = () => {
    localStorage.removeItem('assessmentState');
    dispatch({ type: 'RESET_ASSESSMENT' });
  };

  const getDominantArchetype = () => {
    const scores = state?.archetypeScores;
    const entries = Object.entries(scores);
    if (entries?.length === 0) return null;
    
    return entries?.reduce((max, current) => 
      current?.[1] > max?.[1] ? current : max
    )?.[0];
  };

  const getProgressData = () => {
    return {
      currentQuestion: state?.currentQuestionIndex + 1,
      totalQuestions: state?.totalQuestions,
      completionPercentage: state?.completionPercentage,
      currentScreen: state?.currentScreen
    };
  };

  const value = {
    state,
    startAssessment,
    answerQuestion,
    nextQuestion,
    previousQuestion,
    submitEmail,
    setScreen,
    setTransitioning,
    resetAssessment,
    getDominantArchetype,
    getProgressData
  };

  return (
    <AssessmentContext.Provider value={value}>
      {children}
    </AssessmentContext.Provider>
  );
};

export const useAssessment = () => {
  const context = useContext(AssessmentContext);
  if (!context) {
    throw new Error('useAssessment must be used within AssessmentStateManager');
  }
  return context;
};

export default AssessmentStateManager;